import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grow from '@material-ui/core/Grow'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import MenuList from '@material-ui/core/MenuList'
import styled from 'styled-components'
import Popover from '@material-ui/core/Popover'

import { MenuItem } from './'
import { popperZIndex } from '../../common'

const StyledPopper = styled(Popper)`
  z-index: ${popperZIndex};
`

const StyledPopperOver = styled(Popover)`
  z-index: ${popperZIndex};
`

const ClickElContainer = styled.div`
  display: inline-block;
`

/**
 * Menu is a general menu which is attached to an anchor element.
 *
 * @param {object} props see Menu.propTypes
 * @returns {Menu} - Menu component
 */
const Menu = props => {
  const [open, setOpen] = React.useState(false)
  const anchorElRef = useRef(null)

  useEffect(() => {
    if (props.open !== undefined) {
      setOpen(props.open)
    }
  }, [props.open])

  const onSelect = value => {
    setOpen(prevOpen => {
      if (prevOpen) {
        if (typeof props.onSelect === 'function') {
          setTimeout(props.onSelect, 1, value)
        }
      }
      return !prevOpen
    })
  }

  const onClose = event => {
    if (props.anchorEl && props.anchorEl.contains(event.target)) {
      return
    }
    setOpen(false)
    if (typeof props.onClose === 'function') {
      props.onClose()
    }
  }

  const handleListKeyDown = event => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    }
  }

  const onClick = () => {
    if (open) {
      onClose()
    } else {
      setOpen(true)
    }
  }

  let menuItems = []
  if (Array.isArray(props.items)) {
    menuItems = props.items.map((item, i) => <MenuItem style={item.style} id={item.id} noTranslate={props.noTranslate} key={i} onClick={() => onSelect(item.value)}>{item.label}</MenuItem>)
  }

  if ((!props.anchorOrigin || !props.transformOrigin) && !props.children) {
    return (
      <StyledPopper open={open} anchorEl={props.anchorEl} role={undefined} transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper>
              <ClickAwayListener onClickAway={onClose}>
                <MenuList autoFocusItem={open} onKeyDown={handleListKeyDown}>
                  {menuItems}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </StyledPopper>
    )
  }

  const transformOrigin = props.transformOrigin || { horizontal: 'center', vertical: 'top' }
  const anchorOrigin = props.anchorOrigin || { horizontal: 'center', vertical: 'bottom' }
  return (
    <>
      {props.children && <ClickElContainer ref={anchorElRef} onClick={onClick}>{props.children}</ClickElContainer>}
      <StyledPopperOver 
        open={open} 
        anchorEl={props.anchorEl || anchorElRef.current}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <Paper>
          <ClickAwayListener onClickAway={onClose}>
            <MenuList id={props.id} autoFocusItem={open} onKeyDown={handleListKeyDown}>
              {menuItems}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </StyledPopperOver>
    </>
  )
}

Menu.propTypes = { 
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   * Used to open the menu.
   */ 
  open: PropTypes.bool,
  /**
   * Anchor element for the menu.
   */ 
  anchorEl: PropTypes.object,
  /**
   * Click element for the menu. Clicking this component causes the menu to appear.
   */ 
  children: PropTypes.node,
  /**
   * Function that is called when the menu is closed.
   */ 
  onClose: PropTypes.func,
  /**
   * Function that is called when a menu item is selected.
   */ 
  onSelect: PropTypes.func,
  /**
   * Items for the menu.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  /**
  This is the point on the popover which will attach to the anchor's origin.
   */
  transformOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOf(['center', 'left', 'right']),
    vertical: PropTypes.oneOf(['bottom', 'center', 'top']),
  }),
  /**
  This is the point on the anchor where the popover's anchorEl will attach to.
   */
  anchorOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOf(['center', 'left', 'right']),
    vertical: PropTypes.oneOf(['bottom', 'center', 'top']),
  }),
  /**
   Prevents the language translation of the menu items.
   */
  noTranslate: PropTypes.bool,
}

Menu.defaultProps = {
  noTranslate: false,
}

export default Menu
